import { Css } from "@homebound/beam";
import xor from "lodash/xor";
import { Maybe } from "src/generated/graphqlTypes";
import {
  BidPackageDetailActiveRequestFragment,
  BidPackageDetailPage_BidContractRevisionFragment,
  PageBidPackageDetailFragment,
} from "src/routes/bids/BidPackageDetailPage.generated";
import { AggregateMemberVersionChangeType } from "src/routes/libraries/design-catalog/design-package-configurator/components/DesignUpdatesAvailableButton";

export function getBidPackageTradesWithEstimate(bp: {
  isInternalEstimate: boolean;
  internalEstimateBidContract?: Maybe<{ id: string }>;
  activeRequests: BidPackageDetailActiveRequestFragment[];
  latestVersion: { latestBidContractRevisions: BidPackageDetailPage_BidContractRevisionFragment[] };
}) {
  if (bp.isInternalEstimate) {
    return [
      {
        id: "hbr",
        tradePartner: { id: "hb", name: "Homebound" },
        bidContract: bp.internalEstimateBidContract,
        bidContractRevision: bp.latestVersion.latestBidContractRevisions.first,
      },
    ];
  }
  return bp.activeRequests.map((request) => ({
    ...request,
    bidContractRevision: bp.latestVersion.latestBidContractRevisions.find(
      (bcr) => bcr.bidContract.tradePartner?.id === request.tradePartner.id,
    ),
  }));
}

/** Expensive (# of JSON fields returned) cost codes that use a degraded PBBv1 scope-lines query (see P-2396). */
const HEAVY_SCOPE_COST_CODE_NUMBERS = new Set([
  "5001", // Pre-Fab Cabinets
]);
export function isHeavyScopeBidPackage(bidPackage: PageBidPackageDetailFragment) {
  return bidPackage.costCodes.some((costCode) => HEAVY_SCOPE_COST_CODE_NUMBERS.has(costCode.number));
}

/**
 * These fields determine if a scope line has changed.
 * This has been copied from the v1 component (PlanBasedBidsTable) and now shared with v2 (PlanBasedScopeTable)
 */
type ScopeDiffFields = {
  quantity?: Maybe<number>;
  location?: Maybe<{ id: string }>;
  unitOfMeasure?: Maybe<{ id: string }>;
  materialVariant?: Maybe<{ id: string }>;
  drop?: Maybe<{ id: string }>;
  options?: Maybe<readonly { id: string }[]>;
};
export type ScopeDiff = {
  changeType?: Maybe<string>;
  takeoffLineItemVersion: ScopeDiffFields;
  previous?: Maybe<ScopeDiffFields>;
};
export type ScopeDiffField = keyof ScopeDiffFields;

/**
 * Returns true if a field has been changed given the scope line diff
 */
export function scopeFieldHasChanged(diff: ScopeDiff, field?: ScopeDiffField): boolean {
  if (!diff.previous || !field) return false;
  if (field === "quantity") {
    return diff.previous.quantity !== diff.takeoffLineItemVersion.quantity;
  } else if (field === "options") {
    return optionsHaveChanged(diff);
  }
  const previous = diff.previous[field]?.id;
  const current = diff.takeoffLineItemVersion[field]?.id;
  return previous !== current;
}

/**
 * Apply a Css style to highlight a changed row given the diff field
 */
export function scopeDiffStyles(diff: ScopeDiff, field?: ScopeDiffField) {
  // We want to highlight the whole row if either the row change type is added or if the options on the row have changed
  if (diff.changeType === AggregateMemberVersionChangeType.ADDED || scopeFieldHasChanged(diff, "options")) {
    return Css.bgYellow50.$;
  }
  if (diff.changeType === AggregateMemberVersionChangeType.REMOVED) {
    return Css.bgGray50.tdlt.gray500.$;
  }
  if (diff.changeType === AggregateMemberVersionChangeType.UPDATED && scopeFieldHasChanged(diff, field)) {
    return Css.bgYellow50.$;
  }
}

function optionsHaveChanged(diff: ScopeDiff) {
  const prev = diff.previous;
  if (!prev || diff.changeType !== AggregateMemberVersionChangeType.UPDATED) return false;
  return xor(
    prev.options?.map((o) => o.id),
    diff.takeoffLineItemVersion.options?.map((o) => o.id),
  ).nonEmpty;
}
